import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useCallback, useEffect } from 'react';

import { Box, Button, Skeleton, SkeletonText } from '@chakra-ui/react';

import { FeatureToggleEnum, LoadingState } from '@nocowanie/core';
import { useVisible } from '@nocowanie/common-ui';

import { ApartmentBookButton } from '@app/components/atoms/apartment-book-button';
import { GuestsNightsLabel } from '@app/components/atoms/guests-nights-label';
import { UnavailabilityCTA } from '@app/components/atoms/unavailability-cta';
import { Price } from '@app/components/molecules/price';
import { StickyBar, StickyBarDirection } from '@app/components/molecules/sticky-bar';
import { AppModalsEnum } from '@app/enums';
import { useDrawerState, useIsUnavailable } from '@app/hooks';
import { Discount } from '@app/models/api/api-types';
import { accommodationSlice, commonUiStateSlice, useAppDispatch, useAppSelector } from '@app/state';

import { AccommodationStickyBarProps } from './accommodation-sticky-bar.props';

export const AccommodationStickyBar = ({
    pricingSectionRef,
    isVisible = true,
    ...props
}: AccommodationStickyBarProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'pricing',
    });

    const { push } = useRouter();
    const dispatch = useAppDispatch();

    const currency = useAppSelector(accommodationSlice.selectors.selectCurrency);
    const bestOffer = useAppSelector(accommodationSlice.selectors.selectBestOffer);
    const bestOfferUrl = useAppSelector(accommodationSlice.selectors.selectBestOfferUrl);
    const selectedVariant = useAppSelector(accommodationSlice.selectors.selectSelectedVariant);
    const selectedApartmentId = useAppSelector(
        accommodationSlice.selectors.selectSelectedApartmentId,
    );
    const USE_SHORT_JOURNEY = useAppSelector(
        commonUiStateSlice.selectors.selectUIFtActive<boolean>(FeatureToggleEnum.USE_SHORT_JOURNEY),
    );
    const isModalActive = useAppSelector(commonUiStateSlice.selectors.selectAnyModalActive);
    const isApartmentDetailsModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.ApartmentDetails),
    );
    const isCancellationDetailsModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.CancellationDetails),
    );
    const isGalleryModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.Gallery),
    );
    const isApartmentsListModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.ApartmentsList),
    );
    const isAmenitiesModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.Amenities),
    );
    const isDescriptionModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.Description),
    );
    const isLocationModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.Location),
    );
    const isReviewsModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.Reviews),
    );
    const isOperatorInformationModalActive = useAppSelector<boolean>(
        commonUiStateSlice.selectors.selectModalData(AppModalsEnum.OperatorInformation),
    );

    const showWithModals =
        isModalActive &&
        !isGalleryModalActive &&
        !isApartmentDetailsModalActive &&
        !isCancellationDetailsModalActive &&
        !isOperatorInformationModalActive;

    const loadingState = useAppSelector(accommodationSlice.selectors.selectLoading);
    const isLoading = loadingState === LoadingState.PENDING;

    const isUnAvailable = useIsUnavailable();

    const handleBtnClick = useCallback(() => {
        if (!bestOfferUrl) return;

        push(bestOfferUrl);
    }, [bestOfferUrl]);

    useEffect(() => {
        if (!pricingSectionRef) {
            return;
        }

        const ref = pricingSectionRef?.current;
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: [0, 1],
        };

        const observer = new IntersectionObserver(records => {
            const { isIntersecting } = records[0];
            if (showWithModals) {
                setIsVisibleState(true);
            } else {
                setIsVisibleState(!isIntersecting);
            }
        }, observerOptions);

        if (ref) {
            observer.observe(ref);
        }

        return () => {
            if (ref) {
                observer.unobserve(ref);
            }
        };
    }, [pricingSectionRef, showWithModals]);

    useEffect(() => {
        if (bestOffer) {
            dispatch(accommodationSlice.actions.setSelectedVariant(bestOffer));
        } else {
            dispatch(accommodationSlice.actions.setSelectedVariant(null));
        }
    }, [bestOffer]);

    const { visibleStyles, setIsVisible: setIsVisibleState } = useVisible(
        showWithModals ?? isVisible,
        {
            transform: 'translate3d(0, 0, 0)',
        },
        {
            transform: 'translate3d(0, 100%, 0)',
        },
    );

    const CTAButton = (): JSX.Element => {
        if (selectedVariant && selectedApartmentId) {
            return (
                <ApartmentBookButton
                    apartmentVariant={selectedVariant}
                    apartmentId={selectedApartmentId}
                    variant={'solid'}
                    minW={'9em'}
                />
            );
        }

        if (bestOffer) {
            return (
                <Button variant={'solid'} minW={'9em'} onClick={handleBtnClick}>
                    {t('buttons.book')}
                </Button>
            );
        }

        return (
            <UnavailabilityCTA
                labelsShort
                alignItems={'center'}
                buttonProps={{ minW: '9em', ml: 'auto' }}
                textProps={{ fontSize: 'xs' }}
            />
        );
    };

    const renderPrice = () => {
        if (isLoading) {
            return <SkeletonText noOfLines={2} maxW="144px" flex={1} />;
        }

        if (selectedVariant) {
            return (
                <Price
                    currency={currency ?? ''}
                    price={selectedVariant.convertedTotalPrice ?? 0}
                    basePrice={selectedVariant.convertedTotalBasePrice ?? 0}
                    discounts={selectedVariant.discounts as Discount[]}
                    alignItems="flex-start"
                    my="-2px"
                    lineHeight={1.25}
                >
                    <GuestsNightsLabel
                        adultsCount={selectedVariant.guests}
                        childrenCount={selectedVariant.childrenAges.length}
                    />
                </Price>
            );
        }
    };

    const modalsWithSharedStickyBar = [
        { modal: AppModalsEnum.Amenities, isActive: isAmenitiesModalActive },
        { modal: AppModalsEnum.Description, isActive: isDescriptionModalActive },
        { modal: AppModalsEnum.Location, isActive: isLocationModalActive },
        { modal: AppModalsEnum.Reviews, isActive: isReviewsModalActive },
    ];

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        modalsWithSharedStickyBar.forEach(({ modal, isActive }) => {
            if (isActive) {
                dispatch(commonUiStateSlice.actions.resetModalDataAction(modal));
            }
        });

        setTimeout(() => {
            dispatch(
                commonUiStateSlice.actions.setModalDataAction({
                    modalName: AppModalsEnum.ApartmentsList,
                    modalData: true,
                }),
            );
        }, 0);
    });

    const renderButton = () => {
        return USE_SHORT_JOURNEY && !isApartmentsListModalActive && !isUnAvailable ? (
            <Button
                variant={'solid'}
                minW={'9em'}
                onClick={openDrawer}
                disabled={isDrawerPending}
                loading={isDrawerPending}
            >
                {t('selectRoom')}
            </Button>
        ) : (
            <CTAButton />
        );
    };

    return (
        <StickyBar
            display={'flex'}
            px={'gutterWidth'}
            py={2}
            gap={'gutterWidth'}
            alignItems={'center'}
            justifyContent={'space-between'}
            direction={StickyBarDirection['bottom']}
            transition={'transform .3s, visibility .3s, opacity .3s'}
            css={visibleStyles}
            zIndex={showWithModals ? 'calc({zIndex.modal} + 1)' : 'sticky'}
            {...props}
        >
            {renderPrice()}
            <Box
                flex={'0 0 auto'}
                maxW={'100%'}
                width={!bestOffer && !isLoading ? '100%' : undefined}
            >
                {isLoading ? (
                    <Skeleton w={'144px'}>
                        <Button />
                    </Skeleton>
                ) : (
                    renderButton()
                )}
            </Box>
        </StickyBar>
    );
};
